import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import {
  Alert,
  Autocomplete,
  Box,
  darken,
  DialogContent,
  DialogTitle,
  IconButton,
  lighten,
  MenuItem,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material"

import {
  CloseRounded,
  ExpandLessRounded,
  ExpandMoreRounded,
  FilterAltOffRounded,
  FilterAltRounded,
  LanguageRounded,
  MailRounded,
  PhoneRounded,
  SearchRounded
} from "@mui/icons-material"

import HKVDialog from "../components/HKVDialog/7.0"
import HKVDrawer from "../components/HKVDrawer/14.0"
import HKVEchart from "../components/HKVEChart/2.0"
import HKVLayerControl from "../components/HKVLayerControl/10.0"
import HKVMap, { Legend } from "../components/HKVMap/14.0"
import { destroyTimeDimensionLayer, refreshTimeDimensionLabels } from "../components/HKVMap/utils/functions"

import { GetLocations, GetTimeseries } from "../store/reducers/functions"
import { decrement, increment, reset } from "../store/reducers/loader"
import { addLocalStorage } from "../store/reducers/localStorage"

import { getFewsWebServicesUrl, getGeoServerUrl, getRainsatProxyApiUrl } from "../store/environment"
import { downloadCSV, sortByKey } from "../lib/utils"
import { formatTime, getTimeZoneLabel, isUtcMode, setTimeMode } from "../lib/timezone"

import L from "leaflet"

import { useNavigate, useParams } from "react-router-dom"

import BaseLayerControl from "../components/StyledComponents/BaseLayerControl"

import moment from "moment"

const Rainsat = (props) => {
  const { selectedLanguage } = props

  const dispatch = useDispatch()

  const theme = useTheme()
  const { t } = useTranslation()

  const navigate = useNavigate()
  const { project } = useParams()

  const accountinfo = useSelector((state) => state.accountinfo)

  // claude: weergave-tijdzone (UTC of lokale browsertijd), onthouden in localStorage. Synchroon in de
  // render zetten zodat grafiek en kaart in dezelfde render al de juiste keuze gebruiken (HSGA-204)
  const timeMode = useSelector((state) => state.localStorage?.timeMode) || "utc"
  setTimeMode(timeMode)

  // claude: ref i.p.v. per render een nieuwe controller — anders brak de unmount-cleanup alleen de controller
  // van de eerste render af en liepen requests uit latere renders gewoon door (HSGA-204)
  const controller = useRef(new AbortController()).current

  const [mapRefs, setMapRefs] = useState({})
  const [mapFeatureGroupRefs, setMapFeatureGroupRefs] = useState({})

  const [drawerStates, setDrawerStates] = useState(() => ({
    left: window.innerWidth >= 960
  }))
  const [dialogConfigs, setDialogConfigs] = useState({})

  const [countries, setCountries] = useState()

  const [echartInstance, setEChartInstance] = useState()

  const thresholds = [
    {
      name: "RP1",
      value: {
        Bujumbura: 44,
        Mugere: 74,
        Kanyosha: 74,
        Nthangwa: 69,
        Mutimbuzi: 55,
        Ruzizi: 145
      },
      color: "#B71C1C"
    },
    {
      name: "RP2",
      value: {
        Bujumbura: 50,
        Mugere: 88,
        Kanyosha: 88,
        Nthangwa: 81,
        Mutimbuzi: 60,
        Ruzizi: 159
      },
      color: "#B71C1C"
    },
    {
      name: "RP5",
      value: {
        Bujumbura: 71,
        Mugere: 135,
        Kanyosha: 135,
        Nthangwa: 121,
        Mutimbuzi: 76,
        Ruzizi: 207
      },
      color: "#B71C1C"
    },
    {
      name: "RP10",
      value: {
        Bujumbura: 87,
        Mugere: 170,
        Kanyosha: 170,
        Nthangwa: 149,
        Mutimbuzi: 87,
        Ruzizi: 242
      },
      color: "#B71C1C"
    },
    {
      name: "RP25",
      value: {
        Bujumbura: 108,
        Mugere: 219,
        Kanyosha: 219,
        Nthangwa: 190,
        Mutimbuzi: 103,
        Ruzizi: 291
      },
      color: "#B71C1C"
    },
    {
      name: "RP50",
      value: {
        Bujumbura: 108,
        Mugere: 259,
        Kanyosha: 259,
        Nthangwa: 223,
        Mutimbuzi: 117,
        Ruzizi: 330
      },
      color: "#B71C1C"
    },
    {
      name: "RP100",
      value: {
        Bujumbura: 145,
        Mugere: 301,
        Kanyosha: 301,
        Nthangwa: 259,
        Mutimbuzi: 131,
        Ruzizi: 373
      },
      color: "#B71C1C"
    },
    {
      name: "RP100",
      value: {
        Bujumbura: 166,
        Mugere: 347,
        Kanyosha: 347,
        Nthangwa: 297,
        Mutimbuzi: 146,
        Ruzizi: 419
      },
      color: "#B71C1C"
    }
  ]

  const fluvialBasinNames = ["Mugere", "Kanyosha", "Ntahangwa", "Mutimbuzi", "Ruzizi"]
  const pluvialBasinNames = ["Bujumbura"]

  const [basinData, setBasinData] = useState([])
  const [minimizedAlert, setMinimizedAlert] = useState(false)
  // claude: standaard alle data tonen in de grafiek, ongeacht de actieve National_rainfall_information
  // kaartlaag; via deze toggle kan de gebruiker alsnog naar de per-laag gefilterde weergave schakelen
  const [filterByActiveLayer, setFilterByActiveLayer] = useState(false)

  const [projects, setProjects] = useState([
    {
      country: "Burundi",
      city: "Bujumbura",
      description: "Bujumbura Flood Early Warning System",
      project: "World Meterological Organization (WMO)",
      status: 2,
      require_login: false,
      timeDimension: {
        // MJ: timerange toegevoegd per laag (start en end)
        // aantal dagen * aantal minuten * aantal seconden * 1000 om aan milliseconden te komen
        layer: [
          {
            label: "Gridded_observations",
            subLabel: "(-10d_until_now)",
            legendGroup: "precipitation",
            key: "hsaf_observations", layer: "WMO_Burundi_HSAF-H60B_Observation",
            timeRange: { startOffset: -10 * 24 * 60 * 60 * 1000, endOffset: 0 }
          },
          {
            label: "Gridded_nowcast",
            subLabel: "(now_until_+6h)",
            legendGroup: "precipitation",
            key: "hsaf_nowcast", layer: "WMO_Burundi_HSAF-H60B_Nowcast",
            timeRange: { startOffset: 0, endOffset: 6 * 60 * 60 * 1000 }
          },
          {
            label: "Gridded_forecast",
            subLabel: "(+6h_until_+7d)",
            legendGroup: "precipitation",
            key: "icon_forecast", layer: "WMO_Burundi_ICON_Forecast",
            timeRange: { startOffset: 6 * 60 * 60 * 1000, endOffset: 7 * 24 * 60 * 60 * 1000 }
          }
        ]
      }
    }
  ])
  const [selectedProject, setSelectedProject] = useState()

  const [city, setCity] = useState()
  const [country, setCountry] = useState()

  const [pluvialOptions, setPluvialOptions] = useState([
    { label: "1_Year_Return_Period", layer: "flooddepth_pluvial_return_period_1" },
    { label: "2_Year_Return_Period", layer: "flooddepth_pluvial_return_period_2" },
    { label: "5_Year_Return_Period", layer: "flooddepth_pluvial_return_period_5" },
    { label: "10_Year_Return_Period", layer: "flooddepth_pluvial_return_period_10" },
    { label: "25_Year_Return_Period", layer: "flooddepth_pluvial_return_period_25" },
    { label: "50_Year_Return_Period", layer: "flooddepth_pluvial_return_period_50" },
    { label: "100_Year_Return_Period", layer: "flooddepth_pluvial_return_period_100" },
    { label: "200_Year_Return_Period", layer: "flooddepth_pluvial_return_period_200" }
  ])
  const [pluvial, setPluvial] = useState(pluvialOptions[0])

  const [fluvialOptions, setFluvialOptions] = useState([
    { label: "1_Year_Return_Period", layer: "flooddepth_fluvial_return_period_1" },
    { label: "2_Year_Return_Period", layer: "flooddepth_fluvial_return_period_2" },
    { label: "5_Year_Return_Period", layer: "flooddepth_fluvial_return_period_5" },
    { label: "10_Year_Return_Period", layer: "flooddepth_fluvial_return_period_10" },
    { label: "25_Year_Return_Period", layer: "flooddepth_fluvial_return_period_25" },
    { label: "50_Year_Return_Period", layer: "flooddepth_fluvial_return_period_50" },
    { label: "100_Year_Return_Period", layer: "flooddepth_pluvial_return_period_100" },
    { label: "200_Year_Return_Period", layer: "flooddepth_pluvial_return_period_200" }
  ])
  const [fluvial, setFluvial] = useState(fluvialOptions[0])

  const [lakeOptions, setLakeOptions] = useState([
    { label: "1_Year_Return_Period", layer: "flooddepth_lake_return_period_1" },
    { label: "2_Year_Return_Period", layer: "flooddepth_lake_return_period_2" },
    { label: "5_Year_Return_Period", layer: "flooddepth_lake_return_period_5" },
    { label: "10_Year_Return_Period", layer: "flooddepth_lake_return_period_10" },
    { label: "25_Year_Return_Period", layer: "flooddepth_lake_return_period_25" },
    { label: "50_Year_Return_Period", layer: "flooddepth_lake_return_period_50" },
    { label: "100_Year_Return_Period", layer: "flooddepth_lake_return_period_100" },
    { label: "200_Year_Return_Period", layer: "flooddepth_lake_return_period_200" }
  ])
  const [lake, setLake] = useState(lakeOptions[0])

  const initialMapConfig = {
    key: "main",
    lat: 16.05,
    lng: 47.69,
    zoom: 7,
    minZoom: 3,
    maxZoom: 20,
    baseLayers: [
      {
        vector: false,
        name: "Topographic",
        key: "topographic",
        panel: "baselayers",
        checked: true,
        url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
      },
      {
        vector: false,
        name: "Satellite",
        key: "satellite",
        panel: "baselayers",
        checked: false,
        url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      }
    ],
    layers: [],
    timeDimensionLayers: [],
    controls: [
      { name: "LayersControl", position: "topright" },
      { name: "ScaleControl", position: "bottomright", metric: true },
      { name: "ZoomControl", position: "bottomright" },
      { name: "ResetBoundsControl", position: "bottomright" }
    ]
  }

  const [mapConfig, setMapConfig] = useState(initialMapConfig)

  const inputRef = useRef()
  const countryIconsRef = useRef(new Map())

  useEffect(() => {
    return () => {
      controller.abort()
      dispatch(reset())
    }
  }, [])

  const createRasterLayer = ({ workspace = "", layer_name = "", layer_key = "", layer = "", style = "", panel = "Other layers", active = false, zIndex = 400 }) => {
    return ({
      wms: true,
      name: layer_name,
      key: layer_key,
      unit: "mm",
      panel: panel,
      checked: active,
      zoomTo: false,
      url: `${getGeoServerUrl()}/geoserver/wms`,
      layer: `${workspace}:${layer}`,
      styles: `${workspace}:${style}`,
      layerControl: {
        description: "Lorem Ipsum",
        center: true,
        download: true,
        legend: true,
        opacity: true
      },
      zIndex: zIndex
    })
  }

  const createWFSLayer = ({ workspace = "", layer_name = "", layer_key = "", layer = "", features = null, options = {}, render_legend = null, tooltip = null, callback = null, panel = "Other layers", active = false, zIndex = 400 }) => {
    return ({
      name: layer_name,
      key: layer_key,
      panel: panel,
      checked: active,
      zoomTo: false,
      features: features,
      url: (workspace && layer)
        ? {
          url: `${getGeoServerUrl()}/geoserver/${workspace}/ows?service=WFS`,
          parameters: {
            version: "1.0.0",
            request: "GetFeature",
            srsName: "EPSG:4326",
            typeName: `${workspace}:${layer}`,
            outputFormat: "application/json"
          },
          ...(callback ? { callback: callback } : {})
        } : null,
      options: options,
      renderLegend: render_legend,
      tooltip: tooltip,
      layerControl: {
        description: "Lorem Ipsum",
        center: true,
        download: true,
        legend: true,
        opacity: true
      },
      zIndex: zIndex
    })
  }

  const addCountriesLayer = () => {
    setMapConfig(prevState => {
      const layers = [...prevState.layers]
      const indexOfLayer = layers.findIndex(ly => ly.key === "countries")

      // The features are needed for the country selector and zoom-to-country,
      // so the layer stays in the config and is only hidden when a project is selected.
      if (indexOfLayer > -1) {
        layers[indexOfLayer] = { ...layers[indexOfLayer], inactive: Boolean(project) }
        return { ...prevState, layers }
      }

      layers.push({
        ...createWFSLayer({
          workspace: "rainsat",
          layer_name: "Countries",
          layer_key: "countries",
          layer: "countries",
          options: {
            fillOpacity: 0,
            opacity: 0.3,
            fillColor: "transparent",
            color: theme.palette.primary.main,
            weight: 1
          },
          tooltip: {
            layer: false,
            sticky: true,
            fields: [
              { name: "Name", field: "name" },
              { name: "Timezone", field: "time", formatter: (value) => `UTC + ${value}` }
            ]
          },
          callback: (features) => {
            countryIconsRef.current.clear()
            setCountries(features)
          },
          panel: "Rainsat",
          active: true,
          zIndex: 400
        }),
        inactive: Boolean(project)
      })

      return { ...prevState, layers }
    })
  }

  const createTDLayer = ({ layer_name = "", layer_subtitle = "", layer_key = "", layer = "", legend_group = null, panel = "Rainsat", active = false, autoPlay = false, zIndex = 450, timeRange = null }) => {
    return ({
      name: layer_name,
      subtitle: layer_subtitle,
      key: layer_key,
      unit: "mm/hr",
      type: "time",
      panel: panel,

      // Layers with the same legendGroup share one legend image in the legend panel
      legendGroup: legend_group,

      url: `${getRainsatProxyApiUrl()}/fews/getmap`,
      legendUrl: `${getRainsatProxyApiUrl()}/fews/getlegend`,

      layer: layer,
      styles: "",
      checked: active,

      autoPlay: autoPlay,
      transitionTime: 1000,
      loop: true,
      timeRange: timeRange,

      layerControl: {
        description: "Lorem Ipsum",
        center: false,
        download: false,
        legend: true,
        opacity: true
      },

      zIndex: zIndex
    })
  }

  useEffect(() => {
    addCountriesLayer()

    if (!project) {
      setCity()
      return
    }

    const tmp = projects.find(pr => pr.project?.replaceAll(" ", "-")?.toLowerCase?.() === project)
    setSelectedProject(tmp)
    setCountry(tmp?.country)
    setCity(tmp?.city)
  }, [project])

  useEffect(() => {
    if (!selectedProject || !countries) return
    zoomToCountry(selectedProject.country)
  }, [selectedProject, countries])

  useEffect(() => {
    let cancelled = false

    const buildLayers = async () => {
      if (!city) {
        // No city: clear managed layers, same pattern as the other effects
        setBasinData([])
        setMapConfig(prevState => {
          const managedKeys = ["pluvial_current", "fluvial_current", "basins"]
          return {
            ...prevState,
            layers: prevState.layers.filter(ly => !managedKeys.includes(ly.key))
          }
        })
        return
      }

      // 1. Fetch locations (features)
      dispatch(increment())
      const json = await dispatch(GetLocations(controller.signal, accountinfo?.token))
      dispatch(decrement())
      const features = (json?.hasOwnProperty("features") ? json.features : json) || []
      const filteredFeatures = features.filter(feature => feature?.properties?.hasDataInViewPeriod === true)

      // 2. For each feature, fetch its timeseries, determine returnPeriod, and format for the chart
      const basins = await Promise.all(
        filteredFeatures.map(async (feature) => {
          const locationId = feature.properties.locationId
          const locationName = feature.properties.locationName

          const isRuzizi = locationId?.toLowerCase() === "ruzizi"
          const targetParameterId = isRuzizi
            ? "p.precip.returnperiod.168h"
            : "p.precip.returnperiod.12h"

          const tsJson = await dispatch(
            GetTimeseries(controller.signal, accountinfo?.token, { locationId })
          )

          const getReturnPeriodLabel = (rp) => {
            // if (rp == null) return "-"
            if (rp < 1) return "No_flood_is_expected"
            if (rp < 5) return "Low_flood_hazard"
            if (rp < 25) return "Medium_flood_hazard"
            return "High_flood_hazard"
          }

          // claude: was het maximum over de hele opgevraagde periode (-10d/+7d), waardoor een verhoogde status
          // tot 10 dagen bleef staan. Nu de actuele waarde: het laatste geldige tijdstip t/m nu (HSGA-204)
          const rpHeader = tsJson?.timeSeries?.find(
            ts => ts.header?.parameterId === targetParameterId
          )
          const now = moment().valueOf()
          // claude: FEWS levert tijden in GMT (timeZone 0.0) → als UTC inlezen, anders 2 uur verschoven in Burundi
          const rpValues = (rpHeader?.events || [])
            .map(e => [moment.utc(`${e.date} ${e.time}`, "YYYY-MM-DD HH:mm:ss").valueOf(), parseFloat(e.value)])
            .filter(([ms, v]) => !isNaN(v) && v !== -999 && ms <= now)

          const returnPeriod = rpValues.length > 0 ? rpValues[rpValues.length - 1][1] : null
          const returnPeriodLabel = getReturnPeriodLabel(returnPeriod)

          // Format the rest of the timeseries for the chart (observation/nowcast/forecast/sum)
          const timeseries = formatTimeseriesData(tsJson, locationId)

          return {
            basin: locationName,
            locationId,
            returnPeriod,
            returnPeriodLabel,
            feature,
            timeseries
          }
        })
      )

      const featuresWithReturnPeriod = basins.map(b => ({
        ...b.feature,
        properties: {
          ...b.feature.properties,
          returnPeriod: b.returnPeriod,
          returnPeriodLabel: b.returnPeriodLabel
        }
      }))

      if (cancelled) return

      const resolved = basins//.filter(b => fluvialBasinNames.concat(pluvialBasinNames).findIndex(basin => basin === b.basin) >= 0)// && b.returnPeriod != null)

      setBasinData(resolved)

      // 3. Build raster layers
      const createLayerName = (basin, layerVariant, returnPeriod) => {
        return `${city.toLowerCase()}_${basin.toLowerCase()}_flooddepth_${layerVariant}_return_period_${returnPeriod}`
      }

      const findBasin = (name) => {
        return resolved.find(b => b.basin?.toLowerCase() === name.toLowerCase())
      }

      const pluvialBasins = pluvialBasinNames.map(name => findBasin(name)).filter(b => b && b.returnPeriod > 0)

      const activePluvialLayers = pluvialBasins
        .map(b => createLayerName(b.basin, "pluvial", b.returnPeriod))
        .join(",")
      const activePluvialStyles = activePluvialLayers
        .split(",")
        .map(() => "flooddepth")

      const pluvialHazardLayer = {
        ...createRasterLayer({
          workspace: "rainsat",
          layer_name: "Pluvial_flood_hazard",
          layer_key: "pluvial_current",
          layer: activePluvialLayers,
          style: activePluvialStyles,
          panel: [country, `Flood_hazard_maps_${city}`, "Current_flood_hazard_situation"],
          active: false,
          zIndex: 407
        }),
        inactive: pluvialBasins.length === 0
      }

      const fluvialBasins = fluvialBasinNames.map(name => findBasin(name)).filter(b => b && b.returnPeriod > 0)

      const activeFluvialLayers = fluvialBasins
        .map(b => createLayerName(b.basin, "fluvial", b.returnPeriod))
        .join(",")
      const activeFluvialStyles = activeFluvialLayers
        .split(",")
        .map(() => "flooddepth")

      const fluvialHazardLayer = {
        ...createRasterLayer({
          workspace: "rainsat",
          layer_name: "Fluvial_flood_hazard",
          layer_key: "fluvial_current",
          layer: activeFluvialLayers,
          style: activeFluvialStyles,
          panel: [country, `Flood_hazard_maps_${city}`, "Current_flood_hazard_situation"],
          active: fluvialBasins.length > 0,
          zIndex: 406
        }),
        inactive: fluvialBasins.length === 0
      }

      // 4. Build basins WFS layer
      const basinsLayer = createWFSLayer({
        workspace: "rainsat",
        layer_name: "Basin_aggregated_timeseries",//`${city}_Basin_information`,
        layer_key: "basins",
        features: featuresWithReturnPeriod,
        options: {
          fillColor: "transparent",
          fillOpacity: 0,
          color: theme.palette.primary.dark,
          opacity: 1,
          weight: 2
        },
        // render_legend: () => {
        //   return (
        //     <React.Fragment>
        //       <div style={{ display: "flex", alignItems: "center", gap: theme.spacing(0.5) }}>
        //         <div
        //           style={{
        //             border: `2px solid ${theme.palette.primary.dark}`,
        //             borderRadius: "2px",
        //             width: 12, height: 12
        //           }}
        //         />
        //         <Typography variant="body2">{t("Has_flood_forecast")}</Typography>
        //       </div>
        //       <div style={{ display: "flex", alignItems: "center", gap: theme.spacing(0.5) }}>
        //         <div
        //           style={{
        //             border: "2px solid #8d6e63", //${theme.palette.warning.main}`,
        //             borderRadius: "2px",
        //             width: 12, height: 12
        //           }}
        //         />
        //         <Typography variant="body2">{t("No_flood_forecast")}</Typography>
        //       </div>
        //     </React.Fragment>
        //   )
        // },
        tooltip: {
          layer: false,
          sticky: true,
          name: "Click_for_rainfall_data",
          fields: [
            { name: "Area", field: "locationName" }
          ]
        },
        panel: [country, "National_rainfall_information"],
        active: true,
        zIndex: 510
      })

      if (cancelled) return

      // 5. Merge into mapConfig
      setMapConfig(prevState => {
        const hazardLayers = [
          { key: "pluvial_current", hazardLayer: pluvialHazardLayer },
          { key: "fluvial_current", hazardLayer: fluvialHazardLayer },
          { key: "basins", hazardLayer: basinsLayer }
        ]

        let layers = [...prevState.layers]

        hazardLayers.forEach(({ key, hazardLayer }) => {
          const index = layers.findIndex(ly => ly.key === key)

          if (index > -1) {
            if (hazardLayer) {
              layers[index] = {
                ...layers[index],
                name: hazardLayer.name,
                layer: hazardLayer.layer,
                styles: hazardLayer.styles,
                inactive: hazardLayer.inactive ?? false
              }
            } else {
              layers.splice(index, 1)
            }
          } else {
            if (hazardLayer) {
              layers.push(hazardLayer)
            }
          }
        })

        return { ...prevState, layers }
      })
    }

    buildLayers().catch(err => {
      if (!cancelled) console.error("buildLayers failed", err)
    })

    return () => {
      cancelled = true
    }
  }, [city])

  useEffect(() => {
    let pluvialLayer
    if (city && pluvial) {
      pluvialLayer = {
        ...createRasterLayer({
          workspace: "rainsat",
          layer_name: "Pluvial_floods",//pluvial.label,
          layer_key: "pluvial",
          layer: `${city?.toLowerCase()}_${pluvial.layer}`,
          style: "flooddepth",
          panel: [country, `Flood_hazard_maps_${city}`, "Flood_hazard_maps_archive"],
          active: false,
          zIndex: 404
        }), labelFontSize: "0.9375rem"
      }
    }

    setMapConfig(prevState => {
      const indexOfLayer = prevState.layers.findIndex(ly => ly.key === "pluvial")

      if (indexOfLayer > -1) {
        if (pluvialLayer) {
          prevState.layers[indexOfLayer] = {
            ...prevState.layers[indexOfLayer],
            layer: pluvialLayer.layer
          }
        } else {
          prevState.layers.splice(indexOfLayer, 1)
        }
      } else {
        if (pluvialLayer) {
          prevState.layers.push(pluvialLayer)
        }
      }

      return { ...prevState }
    })
  }, [city, pluvial])

  useEffect(() => {
    let fluvialLayer
    if (city && fluvial) {
      fluvialLayer = {
        ...createRasterLayer({
          workspace: "rainsat",
          layer_name: "Fluvial_floods",//fluvial.label,
          layer_key: "fluvial",
          layer: `${city?.toLowerCase()}_${fluvial.layer}`,
          style: "flooddepth",
          panel: [country, `Flood_hazard_maps_${city}`, "Flood_hazard_maps_archive"],
          active: false,
          zIndex: 403
        }), labelFontSize: "0.9375rem"
      }
    }

    setMapConfig(prevState => {
      const indexOfLayer = prevState.layers.findIndex(ly => ly.key === "fluvial")

      if (indexOfLayer > -1) {
        if (fluvialLayer) {
          prevState.layers[indexOfLayer] = {
            ...prevState.layers[indexOfLayer],
            layer: fluvialLayer.layer
          }
        } else {
          prevState.layers.splice(indexOfLayer, 1)
        }
      } else {
        if (fluvialLayer) {
          prevState.layers.push(fluvialLayer)
        }
      }

      return { ...prevState }
    })
  }, [city, fluvial])

  useEffect(() => {
    let lakeLayer
    if (city && lake) {
      lakeLayer = {
        ...createRasterLayer({
          workspace: "rainsat",
          layer_name: "Lake_floods",
          layer_key: "lake",
          layer: `${city?.toLowerCase()}_${lake.layer}`,
          style: "flooddepth",
          panel: [country, `Flood_hazard_maps_${city}`, "Flood_hazard_maps_archive"],
          active: false,
          zIndex: 402
        }), labelFontSize: "0.9375rem"
      }
    }

    setMapConfig(prevState => {
      const indexOfLayer = prevState.layers.findIndex(ly => ly.key === "lake")

      if (indexOfLayer > -1) {
        if (lakeLayer) {
          prevState.layers[indexOfLayer] = {
            ...prevState.layers[indexOfLayer],
            layer: lakeLayer.layer
          }
        } else {
          prevState.layers.splice(indexOfLayer, 1)
        }
      } else {
        if (lakeLayer) {
          prevState.layers.push(lakeLayer)
        }
      }

      return { ...prevState }
    })
  }, [city, lake])

  const getPadding = (placement) => {
    let padding = [0, 0]
    const drawerEl = document.getElementById(`drawer-${placement}`)

    if (drawerEl) {
      const offset = drawerEl.style.visibility !== "hidden"
        ? drawerEl.offsetWidth
        : 0

      padding[0] = offset
    }
    return padding
  }

  const zoomToCountry = (country, gRef) => {
    const mapRef = mapRefs["main"]
    if (!mapRef) return

    if (country) {
      let selectedLayerBounds

      const groupRef = gRef || mapFeatureGroupRefs["countries"]
      const layers = groupRef?.getLayers?.()

      if (layers && layers.length > 0) {
        // Use the layer group if available
        const selectedLayer = layers.find(
          l => l.feature.properties.name.toLowerCase() === country.toLowerCase()
        )
        if (!selectedLayer) return
        selectedLayerBounds = selectedLayer.getBounds()
      } else if (countries) {
        // Fall back to the countries state
        const selectedFeature = countries.find(
          c => c.properties.name.toLowerCase() === country.toLowerCase()
        )
        if (!selectedFeature) return
        selectedLayerBounds = L.geoJSON(selectedFeature).getBounds()
      } else {
        return
      }

      const sw = selectedLayerBounds.getSouthWest()
      const ne = selectedLayerBounds.getNorthEast()

      const modifiedNe = L.latLng(ne.lat, (ne.lng - 0.4))
      const modifiedBounds = L.latLngBounds(sw, modifiedNe)

      mapRef.flyToBounds(modifiedBounds, {
        duration: 0.15,
        paddingTopLeft: getPadding("left"),
        paddingBottomRight: getPadding("right")
      })
    } else {
      const bounds = L.latLngBounds(
        L.latLng(-40.39617972504841, -22.14393537183129),
        L.latLng(46.80535088596816, 51.936548500915606)
      )

      mapRef.flyToBounds(bounds, {
        duration: 0.15,
        paddingTopLeft: getPadding("left"),
        paddingBottomRight: getPadding("right")
      })
    }
  }

  useEffect(() => {
    if (!mapRefs.main) return

    // claude: side effects (echte Leaflet-cleanup + een tweede setState-call) horen niet in de
    // setMapConfig-updater — die moet puur zijn en kan door React meermaals aangeroepen worden. De oude
    // laag-lijst lezen we nu vóór de state-update, buiten de updater, en de groupRefs ruimen we in één
    // keer op i.p.v. per-laag in een loop.
    const previousLayers = mapConfig.timeDimensionLayers

    previousLayers.forEach(ly => {
      const gRef = mapFeatureGroupRefs[ly.key]
      destroyTimeDimensionLayer(mapRefs.main, gRef)
    })

    if (previousLayers.length > 0) {
      setMapFeatureGroupRefs(prevState => {
        const rest = { ...prevState }
        previousLayers.forEach(ly => { delete rest[ly.key] })
        return rest
      })
    }

    let timeDimensionLayers = []

    if (selectedProject) {
      const projectLayers = selectedProject?.timeDimension?.layer || []

      projectLayers.forEach((layerItem, ix) => {
        timeDimensionLayers.push(
          createTDLayer({
            layer_name: layerItem.label,
            layer_subtitle: layerItem.subLabel,
            layer_key: layerItem.key,
            layer: layerItem.layer,
            legend_group: layerItem.legendGroup ?? null,
            panel: [selectedProject.country, "National_rainfall_information"],
            active: projectLayers.length - 1 === ix,
            autoPlay: false,//projectLayers.length - 1 === ix,
            zIndex: 500 - ix,
            timeRange: layerItem.timeRange ?? null
          })
        )
      })
    } else {
      timeDimensionLayers.push(
        createTDLayer({
          layer_name: "Precipitation",
          layer_key: "precipitation",
          layer: "MTG: HSAF-H40B",
          panel: "Rainsat",
          active: true,
          autoPlay: false,//true,
          zIndex: 440
        })
      )
    }

    setMapConfig(prevState => ({ ...prevState, timeDimensionLayers }))
  }, [mapRefs?.main, selectedProject]) // eslint-disable-line

  // claude: alleen wanneer filterByActiveLayer aanstaat geven we de aangevinkte laag/lagen door aan de
  // grafiek — staat de toggle uit, dan toont de grafiek altijd alle series (observation/nowcast/forecast)
  // voluit. Geeft alle aangevinkte lagen terug (niet slechts de eerste), want de TD-lagen zijn checkboxen
  // en dus niet exclusief — er kunnen meerdere National_rainfall_information-lagen tegelijk aan staan.
  const getActiveTDLayers = () => filterByActiveLayer
    ? (mapConfig.timeDimensionLayers?.filter(l => l.checked) ?? [])
    : []

  // claude: als de actieve neerslag laag (of de filter-toggle) wisselt terwijl het grafiek open staat, update dan de grafiek mee
  useEffect(() => {
    if (!dialogConfigs.precipitation || !echartInstance) return
    const { properties, timeseries } = dialogConfigs.precipitation
    updateEchartData(echartInstance, properties, timeseries, getActiveTDLayers())
  }, [mapConfig.timeDimensionLayers, filterByActiveLayer]) // eslint-disable-line

  // claude: als de basins-laag wordt uitgevinkt terwijl er een basin-feature geselecteerd is, sluit dan
  // de precipitation-dialoog — anders blijft het (nu onzichtbare) feature "geselecteerd" getoond
  useEffect(() => {
    if (!dialogConfigs.precipitation) return
    const basinsLayer = mapConfig.layers?.find(l => l.key === "basins")
    if (!basinsLayer?.checked) {
      closeDialog("precipitation")
    }
  }, [mapConfig.layers]) // eslint-disable-line

  const openDialog = (key, size, options) => {
    const mergedOptions = { ...options, open: true, size: size ? size : "lg" }
    setDialogConfigs(prevState => ({
      ...prevState,
      [key]: mergedOptions
    }))
  }

  const closeDialog = (key) => {
    setDialogConfigs(prevState => ({
      ...prevState,
      [key]: null
    }))
  }

  const mapMount = (mapRef) => {
    setMapRefs(prevState => ({
      ...prevState,
      [mapRef.key]: mapRef
    }))
  }

  const formatTimeseriesData = (json = {}, locationId = "") => {
    const tmp = [
      {
        id: "observation",
        name: t("Satellite_based_rainfall_observation"),
        type: "bar",
        moduleInstanceId: "pr.wmo.burundi.aggregate.MSG.data",
        parameterId: "p.precip.mm",
        qualifierId: "observations"
      },
      {
        id: "nowcast",
        name: t("Nowcast"),
        type: "bar",
        moduleInstanceId: "pr.wmo.burundi.aggregate.MSG.data",
        parameterId: "p.precip.mm",
        qualifierId: "nowcast"
      },
      {
        id: "forecast",
        name: t("ICON_forecast"),
        type: "bar",
        moduleInstanceId: "pr.wmo.burundi.aggregate.icon.data",
        parameterId: "p.precip.accum",
        qualifierId: "ICON forecast"
      },
      {
        id: "sum",
        name: locationId.toLowerCase() === "ruzizi"
          ? t("Cumulative_168h_rainfall_sum")
          : t("Cumulative_12h_rainfall_sum"),
        type: "line",
        moduleInstanceId: "pr.wmo.burundi.merge.rollingsum",
        // claude: 12h-bassins gebruiken "p.precip.mm.12h" zodra FEWS die levert; tot die tijd terugvallen op de
        // uurlijkse merged-reeks (mm/hr), waaruit renderPrecipitationChart zelf de 12h-som berekent (HSGA-204)
        parameterId: locationId.toLowerCase() === "ruzizi"
          ? "p.precip.mm.168h"
          : "p.precip.mm.12h",
        fallbackParameterId: locationId.toLowerCase() === "ruzizi"
          ? null
          : "p.precip.mm",
        qualifierId: "merged"
      }
    ]

    const now = moment().valueOf()

    // Extract raw data per series
    const findTimeserie = (tm, parameterId) => json?.timeSeries?.find(ts =>
      ts.header.moduleInstanceId === tm.moduleInstanceId &&
      ts.header.parameterId === parameterId &&
      ts.header.qualifierId?.includes(tm.qualifierId) // claude: optional chaining toegevoegd — voorkomt crash als qualifierId ontbreekt in de response
    )

    const toData = (timeserie) => timeserie?.events?.map(event => {
      const datetime = `${event.date} ${event.time}`
      // claude: FEWS levert tijden in GMT (timeZone 0.0) → als UTC inlezen i.p.v. browsertijd (HSGA-204)
      const ms = moment.utc(datetime, "YYYY-MM-DD HH:mm:ss").valueOf()
      const value = parseFloat(event.value) === -999 ? null : parseFloat(event.value)
      return [ms, value]
    }).filter(d => d[1] !== null) || []

    const rawSeries = tmp.map(tm => {
      let timeserie = findTimeserie(tm, tm.parameterId) || {}
      let data = toData(timeserie)

      // claude: reeks ontbreekt of bevat alleen missings → fallback-parameter proberen (zie "sum")
      if (data.length === 0 && tm.fallbackParameterId) {
        timeserie = findTimeserie(tm, tm.fallbackParameterId) || {}
        data = toData(timeserie)
      }

      // claude: eenheid en tijdstap meegeven — de observaties zijn intensiteiten (mm/hr) per 15 minuten,
      // dus voor een hoeveelheid in mm moet elke waarde met de tijdstap in uren vermenigvuldigd worden (HSGA-204)
      const timeStep = timeserie?.header?.timeStep
      const timeStepHours = timeStep?.unit === "second" ? parseFloat(timeStep.multiplier) / 3600 : 1

      return { ...tm, data, units: timeserie?.header?.units, timeStepHours }
    })

    // claude: FEWS slaat historische nowcast-runs op; de brede tijdrange (-7d/+7d) leverde daardoor 1346
    // nowcast-events op met exact dezelfde tijdstempels als de observaties. De oude overlap-filter op
    // timestamp-niveau filterde daardoor alles weg. Nieuwe aanpak: gebruik het laatste observatie-tijdstip
    // als grens en toon alleen nowcast/forecast data die ná dat tijdstip valt.
    const observationData = rawSeries.find(s => s.id === "observation")?.data || []
    const maxObservationTs = observationData.reduce((max, [ms]) => Math.max(max, ms), 0)

    return rawSeries.map(s => {
      let data = s.data
      if (s.id === "nowcast" || s.id === "forecast") {
        data = data.filter(([ms]) => ms > maxObservationTs)
      }

      return {
        ...s,
        data,
        total: data.reduce((sum, [, value]) => sum + (value ?? 0), 0)
      }
    })
  }

  const mapFeatureClick = (featureLayer, layer) => {
    if (layer.key === "basins") {
      const { properties } = featureLayer.feature

      const entry = basinData.find(b => b.locationId?.toLowerCase() === properties?.locationId?.toLowerCase())
      const timeseries = entry?.timeseries || []
      // claude: actieve laag/lagen meegeven zodat grafiek en xAxis-bereik overeenkomen met de geselecteerde kaartlaag
      // (alleen relevant als filterByActiveLayer aanstaat, zie getActiveTDLayers)
      const activeLayers = getActiveTDLayers()

      if (properties.locationId === dialogConfigs?.precipitation?.properties?.locationId) {
        closeDialog("precipitation")
      } else {
        openDialog("precipitation", "sm", {
          properties,
          timeseries
        })

        if (dialogConfigs.precipitation && echartInstance) {
          updateEchartData(echartInstance, properties, timeseries, activeLayers)
        }
      }
    }
  }

  const mapFeatureOptions = (layer, featureLayer, featureType, hovered) => {
    const style = mapFeatureStyle(layer, featureLayer, hovered)

    if (!style) return

    let options = {}
    let divIcon, svg

    switch (featureType) {
      case "circle":
        options = style
        break;

      case "marker":
      default:
        svg = `<svg width="100%" height="100%" viewBox="0 0 10 10" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" xml:space="preserve" xmlns:serif="http://www.serif.com/" style="fill:${style.color};fill-rule:evenodd;clip-rule:evenodd;stroke-linejoin:round;stroke-miterlimit:2;"><use id="Background" xlink:href="#_Image1" x="0" y="0" width="8px" height="8px" transform="matrix(0.766518,0,0,1.04482,3.86786,0.805715)"/><path id="outline" d="M4.897,0.815c0.254,0.009 0.507,0.045 0.753,0.117c0.197,0.056 0.385,0.132 0.567,0.228c0.183,0.098 0.356,0.213 0.517,0.346c0.23,0.193 0.427,0.415 0.59,0.665c0.215,0.328 0.357,0.687 0.429,1.072c0.026,0.139 0.043,0.276 0.047,0.415l0,0.153c-0.002,0.149 -0.015,0.306 -0.035,0.468c-0.04,0.286 -0.108,0.563 -0.195,0.843c-0.092,0.281 -0.2,0.556 -0.323,0.824c-0.147,0.319 -0.31,0.63 -0.485,0.933c-0.171,0.29 -0.349,0.571 -0.538,0.85c-0.167,0.247 -0.319,0.446 -0.465,0.639c-0.145,0.194 -0.467,0.589 -0.467,0.589c-0.054,0.072 -0.222,0.229 -0.473,0.229c-0.25,-0 -0.399,-0.128 -0.473,-0.22c0,0 -0.309,-0.399 -0.495,-0.64c-0.149,-0.193 -0.279,-0.365 -0.453,-0.623c-0.205,-0.303 -0.397,-0.611 -0.58,-0.928c-0.102,-0.18 -0.202,-0.363 -0.292,-0.548c-0.142,-0.284 -0.267,-0.571 -0.376,-0.867c-0.072,-0.201 -0.136,-0.404 -0.186,-0.611c-0.04,-0.169 -0.076,-0.342 -0.095,-0.513c-0.013,-0.097 -0.018,-0.194 -0.026,-0.294c-0.007,-0.12 -0.005,-0.24 0.004,-0.359c0.022,-0.334 0.1,-0.658 0.234,-0.965c0.112,-0.259 0.256,-0.495 0.434,-0.713c0.171,-0.208 0.366,-0.389 0.586,-0.545c0.259,-0.183 0.538,-0.32 0.84,-0.413c0.169,-0.052 0.339,-0.088 0.513,-0.108c0.127,-0.016 0.257,-0.026 0.387,-0.025l0.056,0.001Zm2.453,2.851c-0.004,-0.113 -0.018,-0.226 -0.039,-0.339c-0.062,-0.327 -0.182,-0.632 -0.364,-0.91c-0.138,-0.214 -0.307,-0.402 -0.501,-0.565c-0.137,-0.113 -0.284,-0.212 -0.44,-0.295c-0.153,-0.081 -0.313,-0.145 -0.481,-0.193c-0.211,-0.062 -0.426,-0.092 -0.643,-0.099c-0.124,-0.004 -0.249,0.005 -0.373,0.02c-0.149,0.018 -0.293,0.048 -0.436,0.092c-0.256,0.079 -0.493,0.196 -0.712,0.351c-0.186,0.132 -0.353,0.285 -0.498,0.463c-0.152,0.184 -0.274,0.386 -0.369,0.606c-0.113,0.26 -0.179,0.534 -0.199,0.817c-0.007,0.1 -0.009,0.2 -0.003,0.297c0.007,0.089 0.012,0.178 0.024,0.27c0.018,0.156 0.05,0.313 0.086,0.463c0.046,0.19 0.106,0.379 0.171,0.563c0.102,0.279 0.221,0.55 0.356,0.821c0.087,0.178 0.183,0.353 0.28,0.524c0.176,0.306 0.365,0.605 0.562,0.899c0.206,0.304 0.422,0.598 0.646,0.889c0.132,0.171 0.267,0.337 0.405,0.501c0.18,-0.216 0.353,-0.436 0.523,-0.658c0.174,-0.23 0.344,-0.465 0.506,-0.707c0.184,-0.27 0.358,-0.543 0.522,-0.824c0.168,-0.29 0.325,-0.587 0.465,-0.894c0.116,-0.252 0.218,-0.511 0.303,-0.773c0.08,-0.253 0.142,-0.506 0.178,-0.765c0.018,-0.141 0.029,-0.279 0.031,-0.411c-0.002,-0.054 -0.002,-0.098 0,-0.143Zm-2.531,1.731c0.881,0 1.598,-0.714 1.603,-1.595c0.006,-0.888 -0.717,-1.605 -1.592,-1.611c-0.892,-0.006 -1.611,0.719 -1.614,1.599c0,0.887 0.723,1.607 1.603,1.607Zm0,-0.45c-0.634,0 -1.153,-0.518 -1.153,-1.156c0.003,-0.633 0.52,-1.154 1.162,-1.15c0.629,0.004 1.148,0.519 1.144,1.159c-0.003,0.633 -0.52,1.147 -1.153,1.147Z"/><path id="marker" d="M7.575,3.808c-0.002,0.141 -0.014,0.289 -0.033,0.44c-0.038,0.274 -0.103,0.539 -0.186,0.804c-0.089,0.273 -0.194,0.54 -0.313,0.8c-0.144,0.313 -0.304,0.617 -0.475,0.913c-0.168,0.286 -0.344,0.563 -0.531,0.838c-0.084,0.126 -0.203,0.301 -0.324,0.474c-0.114,0.165 -0.242,0.346 -0.39,0.528c-0.361,0.446 -0.641,0.429 -0.998,-0.02c-0.16,-0.203 -0.295,-0.383 -0.397,-0.522c-0.117,-0.16 -0.232,-0.321 -0.343,-0.486c-0.201,-0.299 -0.392,-0.602 -0.571,-0.913c-0.1,-0.176 -0.198,-0.355 -0.287,-0.537c-0.138,-0.277 -0.26,-0.556 -0.365,-0.843c-0.069,-0.193 -0.131,-0.389 -0.179,-0.587c-0.038,-0.16 -0.072,-0.325 -0.091,-0.49c-0.012,-0.093 -0.017,-0.186 -0.024,-0.279c-0.007,-0.11 -0.005,-0.22 0.003,-0.33c0.021,-0.308 0.093,-0.607 0.217,-0.891c0.103,-0.239 0.236,-0.458 0.401,-0.659c0.158,-0.193 0.339,-0.36 0.542,-0.504c0.239,-0.169 0.497,-0.296 0.776,-0.382c0.156,-0.048 0.313,-0.081 0.476,-0.1c0.133,-0.017 0.27,-0.026 0.406,-0.022c0.236,0.008 0.47,0.041 0.699,0.108c0.182,0.052 0.356,0.122 0.523,0.21c0.17,0.091 0.33,0.198 0.478,0.32c0.213,0.179 0.396,0.384 0.547,0.616c0.198,0.303 0.329,0.635 0.396,0.991c0.024,0.127 0.039,0.253 0.043,0.38c-0.002,0.045 -0.002,0.088 0,0.143Zm-2.756,1.364c0.757,0 1.374,-0.614 1.378,-1.371c0.005,-0.764 -0.616,-1.38 -1.368,-1.385c-0.767,-0.005 -1.385,0.618 -1.388,1.375c0,0.762 0.621,1.381 1.378,1.381Z" style="fill:${style.fillColor};fill-rule:nonzero;"/><defs><image id="_Image1" width="8px" height="8px" xlink:href="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAgAAAAICAYAAADED76LAAAACXBIWXMAAA7EAAAOxAGVKw4bAAAAbElEQVQYlYXOuwrCUBCE4S+eqESxTCVW4vu/lZYKFvF2YpoRtHJhWdid+Wf5UyWzwQJLzNI/gg49Nph/CcYS0RY7rNGG+EJtscIhxyGGJ66CKsF3+WHEDY8PYcA9mSccccm+NsnbR3SOu+INExyKFnMAidMQAAAAAElFTkSuQmCC"/></defs></svg>`

        divIcon = L.divIcon({
          html: svg,
          iconSize: [40, 40],
          iconAnchor: [20, 40],
          tooltipAnchor: [0, -40],
          className: "marker"
        })

        options = {
          ...style,
          icon: divIcon
        }
        break;
    }

    featureLayer.setIcon && featureLayer.setIcon(divIcon)

    return options
  }

  const mapFeatureStyle = (layer, featureLayer, hovered) => {
    if (!layer) return null

    let style = JSON.parse(JSON.stringify(layer?.options || {}))

    if (featureLayer.feature.properties.color) {
      style.color = featureLayer.feature.properties.color
    }

    if (layer.key === "basins") {
      const { properties } = dialogConfigs.precipitation || {}
      // if (featureLayer.feature.properties.returnPeriod === null) {
      //   style.color = "#8d6e63"//theme.palette.warning.main
      // }
      if (featureLayer.feature.properties.locationId === properties?.locationId) {
        style.color = darken("#33eaff", 0.2)
        style.weight = ((style.weight || 0) + 1)
      }
    }

    if (!style.color) {
      if (featureLayer instanceof L.Polyline) {
        style.color = theme.palette.grey[600]
      } else {
        style.color = theme.palette.grey[900]
      }
    }

    if (!style.fillColor) {
      style.fillColor = theme.palette.grey[600]
    }

    let mergedStyle = {
      ...layer.options.style,
      ...style
    }

    if (hovered) {
      if (mergedStyle.fillColor && mergedStyle.fillColor !== "transparent") {
        mergedStyle.fillColor = darken(mergedStyle.fillColor, 0.2)
      }
      if (mergedStyle.color && mergedStyle.color !== "transparent") {
        mergedStyle.color = darken(mergedStyle.color, 0.2)
      }

      if (mergedStyle.hasOwnProperty("weight")) {
        mergedStyle.weight = ((mergedStyle.weight || 0) + 1)
      }
    }

    featureLayer.setStyle && featureLayer.setStyle(mergedStyle)

    return mergedStyle
  }

  const mapMountFeatureGroup = (groupRef, layer, isTimeDimensionLayer) => {
    setMapFeatureGroupRefs(prevState => {
      prevState[groupRef.key] = groupRef
      return { ...prevState }
    })
  }

  const mapRemoveFeatureGroup = (key) => {
    const gRef = mapFeatureGroupRefs[key]
    const isTDLayer = mapConfig.timeDimensionLayers?.some(l => l.key === key)
    if (isTDLayer) {
      destroyTimeDimensionLayer(mapRefs.main, gRef)
    }

    setMapFeatureGroupRefs(prevState => {
      const { [key]: _, ...rest } = prevState
      return rest
    })
  }

  const onLayerChange = (layer, layerType, type, allLayersChecked) => {
    setMapConfig(prevState => {
      const updatedLayers = prevState[layerType].map(l => {
        switch (type) {
          case "all":
            return { ...l, checked: !allLayersChecked }

          case "switch":
            return { ...l, disabled: !allLayersChecked }

          case "radio":
            return { ...l, checked: l.key === layer.key }

          case "checkbox":
          default:
            if (l.key === layer.key) {
              return { ...l, checked: !layer.checked }
            }
            return l
        }
      })

      return {
        ...prevState,
        [layerType]: updatedLayers
      }
    })

    const floodArchiveKeys = ["pluvial", "fluvial", "lake"]
    if (floodArchiveKeys.includes(layer.key) && !layer.checked && (type === "checkbox" || type == null)) {
      const mapRef = mapRefs["main"]
      if (mapRef) {
        mapRef.flyToBounds(
          L.latLngBounds(L.latLng(-3.55, 29.15), L.latLng(-3.15, 29.50)),
          {
            duration: 0.25,
            paddingTopLeft: getPadding("left"),
            paddingBottomRight: getPadding("right")
          }
        )
      }
    }
  }

  const drawerStateChanged = (placement) => {
    setDrawerStates(prevState => ({
      ...prevState,
      [placement]: !prevState[placement]
    }))
  }

  // Builds an SVG path + viewBox straight from the WFS geometry, so no
  // pre-rendered "path" property is needed for the country icons.
  const buildCountryIcon = (geometry) => {
    const polygons = geometry?.type === "MultiPolygon"
      ? geometry.coordinates
      : geometry?.type === "Polygon"
        ? [geometry.coordinates]
        : []

    // Only the outer ring of each polygon is drawn, holes are invisible at icon size
    const rings = polygons.map(polygon => polygon?.[0]).filter(ring => ring?.length > 2)
    if (!rings.length) return null

    // Screen coordinates: longitude to the right, latitude flipped (north is up)
    const projected = rings.map(ring => ring.map(([lng, lat]) => [lng, -lat]))

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    projected.forEach(ring => ring.forEach(([x, y]) => {
      if (x < minX) minX = x
      if (x > maxX) maxX = x
      if (y < minY) minY = y
      if (y > maxY) maxY = y
    }))

    const width = maxX - minX
    const height = maxY - minY
    const extent = Math.max(width, height)
    if (!(extent > 0)) return null

    const tolerance = extent / 200
    const round = (value) => Math.round(value * 1000) / 1000

    const d = projected.map(ring => {
      const ringExtent = Math.max(
        Math.max(...ring.map(p => p[0])) - Math.min(...ring.map(p => p[0])),
        Math.max(...ring.map(p => p[1])) - Math.min(...ring.map(p => p[1]))
      )

      // Skip islands too small to be visible, they only add noise and DOM size
      if (ringExtent < extent / 50) return null

      // Drop points that are closer together than one screen pixel would be
      const simplified = ring.filter((point, index) => {
        if (index === 0 || index === ring.length - 1) return true
        const previous = ring[index - 1]
        return Math.abs(point[0] - previous[0]) > tolerance || Math.abs(point[1] - previous[1]) > tolerance
      })

      if (simplified.length < 3) return null

      return `M${simplified.map(([x, y]) => `${round(x)},${round(y)}`).join("L")}Z`
    }).filter(Boolean).join("")

    if (!d) return null

    // Pad the viewBox slightly so the outline is not clipped
    const padding = extent * 0.02

    return {
      d: d,
      viewBox: `${round(minX - padding)} ${round(minY - padding)} ${round(width + padding * 2)} ${round(height + padding * 2)}`
    }
  }

  const getCountryIcon = (countryName, size = 24) => {
    if (!countryName || !countries) return null

    const key = countryName.toLowerCase()

    if (!countryIconsRef.current.has(key)) {
      const match = countries.find(c => c.properties.name?.toLowerCase() === key)
      countryIconsRef.current.set(key, match ? buildCountryIcon(match.geometry) : null)
    }

    const icon = countryIconsRef.current.get(key)
    if (!icon) return null

    return (
      <svg
        viewBox={icon.viewBox}
        preserveAspectRatio="xMidYMid meet"
        style={{ height: size, width: size, flexShrink: 0, marginRight: 8 }}
      >
        <path d={icon.d} fill="#BDBDBD" />
      </svg>
    )
  }

  const renderCountrySelector = () => {
    if (!countries) return null

    const featureLayers = sortByKey(countries.flatMap((country) => {

      const originalObject = {
        id: country.id,
        label: country.properties.name,
        // icon: icon,
        nowcast: false
      }

      // if (country.properties.nowcast) {
      //   const duplicateObject = {
      //     ...originalObject,
      //     nowcast: true
      //   }
      //   return [originalObject, duplicateObject]
      // }

      return [originalObject]
    }), "label", "asc")

    return (
      <Autocomplete
        disablePortal
        options={featureLayers}
        fullWidth
        sx={{ background: "white" }}
        clearIcon={false}
        // claude: geen dropdown-pijltje — dit is een zoek-actie, geen persistente selectie zoals de
        // project-Autocomplete daaronder
        popupIcon={null}
        renderOption={(props, option) => {
          const { key, ...rest } = props
          return (
            <Box key={key} component="li" sx={{ "& > img": { mr: 2, flexShrink: 0 } }} {...rest}>
              {getCountryIcon(option.label)} {option.label}
            </Box>
          )
        }}
        isOptionEqualToValue={(option, value) => Boolean(option?.label === value?.label && option?.nowcast === value?.nowcast)}
        getOptionLabel={(option) => option?.label || ""}
        // value={null}
        value={null}
        onChange={(event, value) => {
          // setCountry(value?.label)
          if (value) {
            inputRef.current?.blur()
            setTimeout(() => {
              zoomToCountry(value?.label)
            }, 0)
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            placeholder={t("Zoom_to_country")}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps?.input,
                startAdornment: (
                  <SearchRounded fontSize="small" sx={{ color: "text.secondary", ml: 0.5, mr: 0.5 }} />
                )
              }
            }}
          />
        )}
      />
    )
  }

  const renderProjectSelector = () => {
    if (!projects) return

    return (
      <Autocomplete
        disablePortal
        options={projects}
        fullWidth
        openOnFocus
        sx={{ background: "white" }}
        getOptionDisabled={(option) => {
          return option.require_login
        }}
        renderOption={(props, option) => {
          const { key, ...rest } = props

          return (
            <Box key={key} component="li" sx={{ display: "flex", alignItems: "center", gap: theme.spacing(1) }} {...rest}>
              <div style={{ display: "flex", flexDirection: "column", gap: theme.spacing(0.5) }}>
                <Typography variant="body1" sx={{ lineHeight: 1 }}>{option.description}</Typography>
                <Typography variant="body2" sx={{ lineHeight: 1 }}>{option.project}</Typography>
                <Typography variant="body2" sx={{ lineHeight: 1 }}>Country: {option.country}</Typography>
                {/* <Typography variant="body2" sx={{ lineHeight: 1 }}>Country: {getCountryIcon(option.country, 18)} {option.country}</Typography> */}
              </div>
            </Box>
          )
        }}
        renderValue={(option, getItemProps) => {
          const { onDelete, ...itemProps } = getItemProps()
          return (
            <Box
              {...itemProps}
              sx={{
                display: "flex", flexDirection: "column",
                margin: "0px !important",
                maxWidth: "100% !important"
              }}>
              {/* {getCountryIcon(option?.country, 18)} */}
              <Typography variant="body2" noWrap sx={{ fontSize: theme.typography.pxToRem(13.75) }}>{option?.project}</Typography>
              <Typography variant="body2" sx={{ fontSize: theme.typography.pxToRem(13.75) }}>Country: {option?.country}</Typography>
            </Box>
          )
        }}
        getOptionLabel={(option) => option?.project}
        isOptionEqualToValue={(option, value) => Boolean(option?.project === value?.project)}
        value={selectedProject ? selectedProject : null}
        onChange={(event, value) => {
          setSelectedProject(value)

          if (value) {
            // zoomToCountry(value.country)
            const path = value?.project?.replaceAll(" ", "-")?.toLowerCase?.()
            navigate(`/projects/${path}`)
          } else {
            closeDialog("precipitation")
            navigate("/")
          }
        }}
        renderInput={(params) => <TextField {...params} label={t("Project")} />}
      />
    )
  }

  // claude: bij het wisselen van tijdzone de kaartlabels en een open grafiek direct bijwerken (HSGA-204)
  useEffect(() => {
    refreshTimeDimensionLabels(mapRefs.main)
    if (!dialogConfigs.precipitation || !echartInstance) return
    const { properties, timeseries } = dialogConfigs.precipitation
    updateEchartData(echartInstance, properties, timeseries, getActiveTDLayers())
  }, [timeMode]) // eslint-disable-line

  const renderTimeModeSelector = () => {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: theme.spacing(1) }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>{t("Time_zone")}</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={timeMode}
          onChange={(event, value) => {
            if (value) dispatch(addLocalStorage({ key: "timeMode", value }))
          }}
          sx={{ background: "white" }}
        >
          <ToggleButton value="utc" sx={{ textTransform: "none", py: 0.25 }}>UTC</ToggleButton>
          <ToggleButton value="local" sx={{ textTransform: "none", py: 0.25 }}>
            {t("Local_time")} ({getTimeZoneLabel(Date.now(), "local")})
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    )
  }

  const renderRiskLevel = () => {
    if (!selectedProject || basinData.length === 0) return

    const hasFlood = basinData.some(item => item.returnPeriod > 0)
    const filteredBasinData = basinData.filter(b => fluvialBasinNames.concat(pluvialBasinNames).findIndex(basin => basin === b.basin) >= 0)

    return (
      <Alert
        severity={hasFlood ? "warning" : "info"}
        sx={{
          position: "absolute",
          top: theme.spacing(2),
          right: theme.spacing(2),
          zIndex: 1,
          boxShadow: theme.shadows[2],
          minWidth: 300,
          ...(!hasFlood && {
            background: "#fafafa"
          })
        }}
        slotProps={{
          message: { sx: { width: "100%" } }
        }}
        action={
          <IconButton
            size="small"
            color="inherit"
            onClick={() => setMinimizedAlert((prevState) => !prevState)}
          >
            {minimizedAlert ? <ExpandMoreRounded fontSize="small" /> : <ExpandLessRounded fontSize="small" />}
          </IconButton>
        }
      >
        <Typography>{t("Current_flood_hazard_situation")}</Typography>
        {!minimizedAlert && (
          <React.Fragment>
            <Typography variant="body2">{t("City")}: {selectedProject.city}</Typography>
            <table className="defaulttable" style={{ border: `1px solid ${theme.palette.divider}` }}>
              <thead style={{ background: lighten(theme.palette.primary.main, 0.8) }}>
                <tr>
                  <th style={{ padding: theme.spacing(0.75, 1), textAlign: "left" }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{t("Area")}</Typography>
                  </th>
                  <th style={{ padding: theme.spacing(0.75, 1), textAlign: "left" }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{t("Flood_hazard_level")}</Typography>
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* {basinData.filter(b => b.returnPeriod !== null).map((b, ix) => ( */}
                {filteredBasinData.map((b, ix) => (
                  <tr key={ix}>
                    <td style={{ padding: theme.spacing(0.5, 1) }}>
                      <Typography variant="body2" sx={{ textTransform: "capitalize" }}>{b.basin}</Typography>
                    </td>
                    <td style={{ padding: theme.spacing(0.5, 1) }}>
                      <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                        {t(b.returnPeriodLabel) ?? "-"}
                      </Typography>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </React.Fragment>
        )}
      </Alert>
    )
  }

  // claude: activeSeriesIds toegevoegd — de actieve serie(s) krijgen volle opaciteit, de rest wordt gedimmd (0.2).
  // Kan meer dan 1 id bevatten omdat TD-kaartlagen checkboxen zijn (niet exclusief) — er kunnen dus
  // meerdere National_rainfall_information-lagen tegelijk aangevinkt staan.
  // claude: sum-serie uitgefilterd — de cumulatieve lijn wordt niet meer getoond in het grafiek
  const createPrecipitationSeries = (timeseries, thresholds, basin, activeSeriesIds = []) => {
    return timeseries.filter(ts => ts.data?.length > 0 && ts.id !== "sum").map(ts => ({
      name: t(ts.name),
      id: ts.id,
      type: ts.type || "bar",
      data: ts.data,
      emphasis: {
        focus: "series"
      },
      ...(ts.type === "bar" && {
        barGap: "-100%"
      }),
      ...(ts.type === "bar" && activeSeriesIds.length > 0 && {
        itemStyle: { opacity: activeSeriesIds.includes(ts.id) ? 1 : 0.2 }
      }),
      ...(ts.id === "sum") && {
        markLine: {
          silent: true,
          symbol: "none",
          lineStyle: { type: "dashed", width: 1.5 },
          label: {
            position: "end",
            formatter: "{b}",
            fontSize: 11,
            fontWeight: 400
          },
          // claude: drempelwaarden met yAxis: null gefilterd — ECharts crasht anders met "Cannot read properties of undefined (reading 'coord')"
          data: thresholds
            .map(threshold => ({
              name: threshold.name,
              yAxis: threshold.value?.[basin] ?? null,
              lineStyle: { color: threshold.color },
              label: { color: threshold.color }
            }))
            .filter(d => d.yAxis != null)
        }
      }
    }))
  }

  // claude: activeLayers (array van volledige layer-objecten, 1 per aangevinkte National_rainfall_information
  // kaartlaag) toegevoegd als parameter zodat:
  //   1. de xAxis de gecombineerde timeRange van alle aangevinkte lagen dekt (niet slechts de eerste)
  //   2. de bijbehorende dataserie(s) gehighlight worden (via activeSeriesIds in createPrecipitationSeries)
  //   3. achtergrondzone van de actieve periode(s) sterker gekleurd is
  const updateEchartData = (instance, properties, timeseries = [], activeLayers = []) => {
    if (!instance) return

    const LAYER_TO_SERIES = {
      hsaf_observations: "observation",
      hsaf_nowcast: "nowcast",
      icon_forecast: "forecast"
    }
    const activeSeriesIds = activeLayers
      .map(l => LAYER_TO_SERIES[l.key])
      .filter(Boolean)

    const now = moment().valueOf()
    // claude: xAxis min/max = de unie van de timeRanges van alle aangevinkte lagen. Heeft één van de
    // aangevinkte lagen geen timeRange (bv. de generieke fallback-laag buiten een project), dan kunnen
    // begrensde en onbegrensde ranges niet zinvol gecombineerd worden — toon dan alles (geen restrictie).
    const layersWithRange = activeLayers.filter(l => l.timeRange)
    const hasUnrestrictedActiveLayer = activeLayers.some(l => !l.timeRange)
    const xAxisMin = (layersWithRange.length > 0 && !hasUnrestrictedActiveLayer)
      ? now + Math.min(...layersWithRange.map(l => l.timeRange.startOffset))
      : undefined
    const xAxisMax = (layersWithRange.length > 0 && !hasUnrestrictedActiveLayer)
      ? now + Math.max(...layersWithRange.map(l => l.timeRange.endOffset))
      : undefined

    const baseSeries = createPrecipitationSeries(timeseries, thresholds, properties?.basin, activeSeriesIds)

    // Compute zone boundaries from actual data timestamps
    const getRange = (id) => {
      const s = timeseries.find(ts => ts.id === id)
      if (!s?.data?.length) return null
      const tss = s.data.map(([ms]) => ms)
      return [Math.min(...tss), Math.max(...tss)]
    }

    const ZONE_DEFS = [
      { id: "observation", label: "Observation", color: "#78909C" },
      { id: "nowcast", label: "Nowcast", color: "#29B6F6" },
      { id: "forecast", label: "Forecast", color: "#66BB6A" }
    ]

    const markAreaData = ZONE_DEFS
      .map(z => ({ ...z, range: getRange(z.id) }))
      .filter(z => z.range)
      .map(z => [
        {
          name: z.label,
          xAxis: z.range[0],
          itemStyle: { color: activeSeriesIds.includes(z.id) ? `${z.color}33` : `${z.color}1A` },
          label: { color: z.color }
        },
        { xAxis: z.range[1] }
      ])

    // claude: zones en NOW-lijn aan de sum-serie koppelen (heeft altijd data). Een aparte lege hulp-serie
    // werd eerder gebruikt maar ECharts rendert markArea/markLine niet op series met data: [].
    const targetId = baseSeries.some(s => s.id === "sum") ? "sum" : (baseSeries[0]?.id ?? null)

    const nowLine = {
      name: "Now",
      xAxis: now,
      lineStyle: { type: "solid", color: "#546E7A", width: 1.5 },
      label: { formatter: "Now", color: "#546E7A", fontSize: 9 }
    }

    const series = baseSeries.map(s => {
      if (s.id !== targetId) return s

      const existingML = s.markLine ?? { silent: true, symbol: "none", lineStyle: {}, label: {} }

      return {
        ...s,
        markLine: {
          ...existingML,
          data: [...(existingML.data || []), nowLine]
        },
        ...(markAreaData.length > 0 && {
          markArea: {
            silent: true,
            label: { show: true, position: "insideTop", fontSize: 9, fontWeight: 500 },
            data: markAreaData
          }
        })
      }
    })

    const fileName = properties?.basin
      ? `${properties.basin}_precipitation_${moment().format("YYYY-MM-DD")}`
      : `precipitation_${moment().format("YYYY-MM-DD")}`

    const getThresholdLabel = (value) => {
      const crossed = thresholds
        .filter(t => value >= t.value)
        .sort((a, b) => b.value - a.value)
      return crossed.length > 0 ? crossed[0].name : null
    }

    // claude: grafiek in UTC tonen, consistent met de kaart ("… UTC") en FEWS (GMT) (HSGA-204)
    const timeZoneLabel = getTimeZoneLabel()

    const option = {
      useUTC: isUtcMode(),
      animation: false,
      backgroundColor: "rgb(252, 252, 252)",
      textStyle: {
        fontFamily: "'Inter Variable', Inter, Roboto, 'Helvetica Neue', Arial, sans-serif",
        fontSize: 14,
        fontWeight: 500
      },
      grid: { top: 50, right: 50, bottom: 0, left: 0 },
      tooltip: {
        trigger: "axis",
        // axisPointer: {
        //   type: "shadow",
        // },
        formatter: (params) => {
          const time = params[0]?.data[0]
          const formattedTime = `${formatTime(time)} ${getTimeZoneLabel(time)}`

          const rows = params.map(param => {
            const rainfall = param.data[1]
            const threshold = getThresholdLabel(rainfall)

            const formattedRainfall = rainfall?.toLocaleString("nl-NL", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2
            }) ?? 0

            return `
              ${param.marker} ${param.seriesName}: <strong>${formattedRainfall} mm</strong>
              ${threshold ? `<br/><span style="color:${thresholds.find(t => t.name === threshold).color}">⚠ ${threshold}</span>` : ""}
            `
          }).join("<br/>")

          return `
            <div>
              <strong>${formattedTime}</strong><br/>
              ${rows}
            </div>
          `
        }
      },
      axisPointer: {
        lineStyle: {
          width: 2,
          color: theme.palette.grey[600]
        }
      },
      toolbox: {
        show: true,
        showTitle: false,
        top: -8,
        right: -8,
        itemSize: 14,
        feature: {
          myRestoreZoom: {
            show: true,
            title: "Restore zoom",
            icon: "path://M-6.9994 0.9083M4.5416 -2.9654L7.1597 -2.9654L7.1597 -5.6904M7.0261 -2.6448C5.9041 -5.6102 2.8051 -7.8543 -0.8549 -7.4536C-4.6485 -7.0261 -7.5872 -3.6867 -7.4536 0.1603M-4.5416 2.9654L-7.1864 2.9654L-7.1864 5.6904M-7.0261 2.6448C-5.9041 5.6102 -2.8051 7.8543 0.8549 7.4536C4.6485 7.0261 7.5872 3.6867 7.4536 -0.1603",
            onclick: () => {
              instance.dispatchAction({
                type: "dataZoom",
                start: 0,
                end: 100
              })
            }
          },
          saveAsImage: {
            show: true,
            type: "png",
            name: fileName,
            title: "Save as image"
          },
          myDownload: {
            show: true,
            title: "Download CSV",
            icon: "path://M-3.683 -3.4018L0.4688 -3.4018M-3.683 -3.4018L0.4688 -3.4018M3.7902 -0.1339L-3.7098 -0.1339M-5.2902 -7.5L-5.2902 7.5L5.2902 7.5L5.2902 -4.0714L1.9152 -7.5L-5.2902 -7.5ZM1.9152 -7.4464L1.9152 -4.0446L5.2902 -4.0446M3.7902 3.1339L-3.7098 3.1339",
            onclick: () => {
              const csvData = series?.flatMap(serie => serie?.data?.map(d => ({
                name: serie.name,
                datetime: d[0],
                rainfall: d[1]
              }))) || []

              downloadCSV(csvData, fileName, {
                headerFormatter: {
                  name: "Name",
                  datetime: `Datetime (${timeZoneLabel})`,
                  rainfall: "Rainfall [mm/hr]"
                },
                dataFormatter: {
                  datetime: (value) => formatTime(value)
                },
                columnOrder: ["name", "datetime", "rainfall"]
              })
            }
          }
        },
        tooltip: {
          show: true,
          formatter: function (param) {
            return `<div>${param.title}</div>`
          },
          backgroundColor: "transparent",
          padding: 0,
          confine: true,
          extraCssText: "box-shadow: unset;margin-top:-8px;",
          textStyle: {
            fontSize: 14,
            color: "#6578ba",
            fontWeight: 400
          }
        }
      },
      legend: {
        top: 8,
        right: 28 * 3, // 30 per toolbox item
        itemGap: 6,
        itemWidth: 16,
        itemHeight: 12,
        textStyle: {
          fontWeight: 400,
          fontSize: 12
        }
      },
      dataZoom: [
        {
          type: "inside",
          // filterMode: "none",
          min: 0, max: 100
        }
      ],
      xAxis: {
        type: "time",
        name: `${t("Time")} (${timeZoneLabel})`,
        nameLocation: "center",
        scale: false,
        min: xAxisMin,
        max: xAxisMax,
        axisLabel: {
          formatter: (value) => formatTime(value, "DD/MM HH:mm"),
          fontSize: 10,
          rotate: 45
        }
      },
      yAxis: {
        name: "Rainfall [mm/hr]",
        nameLocation: "center",
        scale: true
      },
      series: series,
      color: [
        "#78909C",  // observation - blue-grey, neutral/historical
        "#29B6F6",  // nowcast    - light blue, current/live
        "#66BB6A",  // forecast   - green, future/predicted
        "#AB47BC",  // sum        - purple, derived/aggregate
      ]
    }

    instance.setOption(option, {
      replaceMerge: ["toolbox", "xAxis", "yAxis", "series"]
    })
  }

  const chartDidMount = (instance) => {
    setEChartInstance(instance)

    const { properties, timeseries } = dialogConfigs.precipitation
    // claude: actieve laag/lagen meegeven zodat grafiek direct in de juiste tijdsrange en highlighting opent
    // (alleen relevant als filterByActiveLayer aanstaat, zie getActiveTDLayers)
    updateEchartData(instance, properties, timeseries, getActiveTDLayers())
  }

  const renderPrecipitationChart = () => {
    if (!selectedProject) return

    if (!dialogConfigs.precipitation) return

    const { properties, minimized, timeseries } = dialogConfigs.precipitation

    // claude: observationTotal = directe optelling van observatie-waarden in [now - horizon, now].
    // De rolling sum serie heeft geen punten vóór nu (het venster begint pas bij de eerste forecast),
    // dus sumData.filter(ms <= now) geeft altijd een lege array → gebruik de ruwe observatiebars.
    // forecastTotal = sum-waarde dichtstbij nu+horizon (rolling sum wél beschikbaar voor de toekomst).
    const isRuzizi = properties?.locationId?.toLowerCase?.() === "ruzizi"
    const horizonMs = (isRuzizi ? 168 : 12) * 60 * 60 * 1000
    const now = Date.now()
    const sumSeries = timeseries?.find(ts => ts.id === "sum")
    const sumData = sumSeries?.data || []

    // claude: observaties zijn mm/hr per 15 min — zonder omrekening naar mm werd het totaal 4x te hoog (HSGA-204)
    const obsSeries = timeseries?.find(ts => ts.id === "observation")
    const obsFactor = obsSeries?.units === "mm/hr" ? (obsSeries?.timeStepHours || 1) : 1
    const observationTotal = obsSeries?.data
      ?.filter(([ms]) => ms >= (now - horizonMs) && ms <= now)
      .reduce((acc, [, v]) => acc + (v ?? 0) * obsFactor, 0) ?? 0

    const futureTarget = now + horizonMs
    const closestFuturePoint = sumData.reduce((best, point) =>
      !best || Math.abs(point[0] - futureTarget) < Math.abs(best[0] - futureTarget) ? point : best
      , null)
    // claude: is de sum-reeks de uurlijkse fallback in mm/hr (geen 12h-reeks in FEWS), dan zelf optellen over
    // (now, now + horizon] i.p.v. één uurwaarde als "verwachte neerslag" te tonen (HSGA-204)
    const forecastTotal = sumSeries?.units === "mm/hr"
      ? sumData
        .filter(([ms]) => ms > now && ms <= futureTarget)
        .reduce((acc, [, v]) => acc + (v ?? 0) * (sumSeries.timeStepHours || 1), 0)
      : closestFuturePoint ? (closestFuturePoint[1] ?? 0) : 0
    const formattedObservationTotal = observationTotal.toLocaleString("nl-NL", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    })

    const formattedForecastTotal = forecastTotal.toLocaleString("nl-NL", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    })

    return (
      <HKVDialog
        config={dialogConfigs.precipitation}
        onClose={() => closeDialog("precipitation")}
        draggable={true}
      >
        <DialogTitle
          className="draggable-dialog-title"
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "move",
            padding: theme.spacing(1),
            fontSize: theme.typography.pxToRem(18)
          }}
        >
          {t("Rainfall")} | {t("Area")}: {properties?.locationName || "-"}
          <div style={{ display: "flex", alignItems: "center", gap: theme.spacing(1), marginLeft: theme.spacing(2) }}>
            {/* <Tooltip title={t(filterByActiveLayer ? "Filter_by_active_layer" : "Show_all_data")} disableInteractive>
              {filterByActiveLayer
                ? <FilterAltRounded
                  className="transition"
                  sx={{ background: "#eee", borderRadius: "50%", padding: theme.spacing(0.5), opacity: 0.6, cursor: "pointer", "&:hover": { opacity: 1 } }}
                  onClick={() => setFilterByActiveLayer(false)}
                />
                : <FilterAltOffRounded
                  className="transition"
                  sx={{ background: "#eee", borderRadius: "50%", padding: theme.spacing(0.5), opacity: 0.6, cursor: "pointer", "&:hover": { opacity: 1 } }}
                  onClick={() => setFilterByActiveLayer(true)}
                />
              }
            </Tooltip> */}
            {minimized
              ? <ExpandLessRounded
                className="transition"
                sx={{ background: "#eee", borderRadius: "50%", padding: theme.spacing(0.5), opacity: 0.6, cursor: "pointer", "&:hover": { opacity: 1 } }}
                onClick={() => {
                  setDialogConfigs(prevState => ({
                    ...prevState,
                    "precipitation": {
                      ...prevState.precipitation,
                      minimized: false
                    }
                  }))
                }}
              />
              : <ExpandMoreRounded
                className="transition"
                sx={{ background: "#eee", borderRadius: "50%", padding: theme.spacing(0.5), opacity: 0.6, cursor: "pointer", "&:hover": { opacity: 1 } }}
                onClick={() => {
                  setDialogConfigs(prevState => ({
                    ...prevState,
                    "precipitation": {
                      ...prevState.precipitation,
                      minimized: true
                    }
                  }))
                }}
              />
            }
            <CloseRounded
              className="transition"
              sx={{ background: "#eee", borderRadius: "50%", padding: theme.spacing(0.5), opacity: 0.6, cursor: "pointer", "&:hover": { opacity: 1 } }}
              onClick={() => closeDialog("precipitation")}
            />
          </div>
        </DialogTitle>
        {minimized
          ? null
          : <DialogContent sx={{ display: "flex", flexDirection: "column", overflow: "visible", padding: theme.spacing(0, 1, 1) }}>
            <Typography variant="caption">{t("Cumulative_rainfall_in_the_past")} {isRuzizi ? 168 : 12} {t("hours")} = <b>{formattedObservationTotal}</b> mm</Typography>
            {/* claude: locationId ipv locationName — moet consistent zijn met de check in formatTimeseriesData */}
            <Typography variant="caption">{t("Expected_rainfall_in_the_coming")} + {properties?.locationId?.toLowerCase?.() === "ruzizi" ? 168 : 12} {t("hours")} = <b>{formattedForecastTotal}</b> mm</Typography>
            <HKVEchart
              spec="precipitation"
              chartDidMount={chartDidMount}
              width="100%"
              height={250}
            />
          </DialogContent>
        }
      </HKVDialog>
    )
  }

  const selectSx = {
    fontSize: theme.typography.pxToRem(14)
  }

  const renderPluvialSelect = () => {
    return (
      <TextField
        select
        fullWidth
        size="small"
        value={pluvial.layer}
        label={t("Return_Period")}
        onChange={(event) => {
          setPluvial(pluvialOptions.find(opt => opt.layer === event.target.value))
        }}
        sx={{
          "& .MuiInputBase-input": selectSx,
          "& .MuiInputLabel-root": { ...selectSx },
          "& .MuiInputLabel-shrink": { marginTop: theme.spacing(0.5) },
          "& legend": { fontSize: theme.typography.pxToRem(11) }
        }}
      >
        {pluvialOptions.map((option, ix) => (
          <MenuItem
            key={ix} value={option.layer}
            sx={{
              ...selectSx,
              padding: theme.spacing(0.5, 1.25)
            }}
          >
            {t(option.label)}
          </MenuItem>
        ))}
      </TextField>
    )
  }

  const renderFluvialSelect = () => {
    return (
      <TextField
        select
        fullWidth
        size="small"
        value={fluvial.layer}
        label={t("Return_Period")}
        onChange={(event) => {
          setFluvial(fluvialOptions.find(opt => opt.layer === event.target.value))
        }}
        sx={{
          "& .MuiInputBase-input": selectSx,
          "& .MuiInputLabel-root": { ...selectSx },
          "& .MuiInputLabel-shrink": { marginTop: theme.spacing(0.5) },
          "& legend": { fontSize: theme.typography.pxToRem(11) }
        }}
      >
        {fluvialOptions.map((option, ix) => (
          <MenuItem
            key={ix} value={option.layer}
            sx={{
              ...selectSx,
              padding: theme.spacing(0.5, 1.25)
            }}
          >
            {t(option.label)}
          </MenuItem>
        ))}
      </TextField>
    )
  }

  const renderLakeSelect = () => {
    return (
      <TextField
        select
        fullWidth
        size="small"
        value={lake.layer}
        label={t("Return_Period")}
        onChange={(event) => {
          setLake(lakeOptions.find(opt => opt.layer === event.target.value))
        }}
        sx={{
          "& .MuiInputBase-input": selectSx,
          "& .MuiInputLabel-root": { ...selectSx },
          "& .MuiInputLabel-shrink": { marginTop: theme.spacing(0.5) },
          "& legend": { fontSize: theme.typography.pxToRem(11) }
        }}
      >
        {lakeOptions.map((option, ix) => (
          <MenuItem
            key={ix} value={option.layer}
            sx={{
              ...selectSx,
              padding: theme.spacing(0.5, 1.25)
            }}
          >
            {t(option.label)}
          </MenuItem>
        ))}
      </TextField>
    )
  }

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      {renderRiskLevel()}
      <HKVDrawer
        placement="left"
        open={drawerStates.left}
        header={{
          title: "Rainsat",
          button: {
            visible: true,
            position: "absolute"
          }
        }}
        drawerStateChanged={drawerStateChanged}
      >
        <div
          style={{
            display: "flex", flexDirection: "column",
            gap: theme.spacing(1),
            margin: theme.spacing(1, 1, 0)
          }}
        >
          {renderCountrySelector()}
          {renderProjectSelector()}
          {renderTimeModeSelector()}
          <HKVLayerControl
            mapConfig={mapConfig}
            mapFeatureGroupRefs={mapFeatureGroupRefs}
            mapRef={mapRefs.main}
            onLayerChange={onLayerChange}
            openAccordions={[country, "Rainsat", `Flood_hazard_maps_${city}`, "Current_flood_hazard_situation", "National_rainfall_information"]}
            setMapConfig={setMapConfig}
            baseLayers={false}

            renderPluvialSelect={renderPluvialSelect}
            renderFluvialSelect={renderFluvialSelect}
            renderLakeSelect={renderLakeSelect}
            panelSubtitels={{ Current_flood_hazard_situation: "Current_flood_hazard_situation_subtitle" }}
            panelTooltips={{ Current_flood_hazard_situation: "Current_flood_hazard_situation_tooltip" }}
          />
        </div>
        <div>
          <Paper sx={{ margin: theme.spacing(1), padding: theme.spacing(1) }}>
            <Typography>{t("Contact")} HKV</Typography>
            <div style={{ display: "flex", gap: theme.spacing(0.75), alignItems: "center" }}>
              <img src="/static/images/hkv-logo.png" style={{ flex: 1, minWidth: 0, maxHeight: 64, objectFit: "contain" }} />
              <div style={{ display: "flex", flexDirection: "column", flexShrink: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: theme.spacing(0.25) }}>
                  <PhoneRounded fontSize="small" />
                  <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}><a href="tel:31320294242">+31(0)320 294 242</a></Typography>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: theme.spacing(0.25) }}>
                  <MailRounded fontSize="small" />
                  <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}><a href="mailto:info@hkv.nl">info@hkv.nl</a></Typography>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: theme.spacing(0.25) }}>
                  <LanguageRounded fontSize="small" />
                  <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}><a href="https://www.hkv.nl/en" target="_blank">hkv.nl/en</a></Typography>
                </div>
              </div>
            </div>
          </Paper>
        </div>
      </HKVDrawer>
      <HKVMap
        config={mapConfig}
        dispatch={dispatch}
        mapFeatureStyle={mapFeatureStyle}
        mapFeatureOptions={mapFeatureOptions}
        mapFeatureClick={mapFeatureClick}
        mapMount={mapMount}
        mapMountFeatureGroup={mapMountFeatureGroup}
        mapRemoveFeatureGroup={mapRemoveFeatureGroup}
        selectedFeatureId={dialogConfigs.precipitation?.properties?.locationId}
        selectedLanguage={selectedLanguage}
      />
      <Legend
        mapConfig={mapConfig}
        setMapConfig={setMapConfig}
        selectedLanguage={selectedLanguage}

        tmp={drawerStates.left}
      />
      <BaseLayerControl
        baseLayers={mapConfig.baseLayers}
        onLayerChange={onLayerChange}
      />
      {renderPrecipitationChart()}
    </div>
  )

}
export default Rainsat